import { LinkButton, Tooltip } from '@pmi/dsm-react';
import { PlusDefaultOutlineIcon } from '@pmi/dsm-react/dist/components/icons';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { CategoryType } from '../../../shared/components/CategoryLabel/models';
import selectContainerStyles from '../../../shared/components/InviteWizard/selectAgreement.module.scss';
import WizardCategoryLabel from '../../../shared/components/InviteWizard/WizardCategoryLabel';
import RenderHtml from '../../../shared/components/RenderHtml';
import { AccessRuleItems } from '../../../shared/models/AccessRule';
import { AgreementApiModel } from '../../../shared/models/DealModel';
import { accountSelector } from '../../../shared/store/shared/accountSlice';
import styles from './access-rules.module.scss';
import AddProductsToDomainModal from './AddProductsToDomainModal';
import { useGetDomainProductsQuery } from './apiSlice';

interface AccessRuleProductsProps {
  rule: AccessRuleItems;
  deal: AgreementApiModel;
  readOnly?: boolean;
  onEditModeChange?: (editing: boolean) => void;
}

export default function AccessRuleProductsTab({
  rule,
  deal,
  readOnly = false,
  onEditModeChange = undefined,
}: AccessRuleProductsProps) {
  const [showAddProductsModal, setShowAddProductsModal] = useState(false);
  const { currentAccountId } = useSelector(accountSelector);
  const { id, isActive, providerNumber } = rule;
  const { data } = useGetDomainProductsQuery({
    accountId: currentAccountId,
    providerNumber,
    accessRuleId: id,
  });

  const domainProducts = data ?? [];

  // Get only the domain products with full info from deal.products
  const assignedProductsWithInfo = domainProducts
    .map((domainProduct) =>
      (deal.products ?? []).find(
        (p) => p.accountProductId === domainProduct.accountProductId,
      ),
    )
    .filter((product) => product !== undefined);

  // Group assigned products by type
  const productsByType = assignedProductsWithInfo.reduce(
    (acc: Record<string, any[]>, product: any) => {
      const type = product.type || 'Other';
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(product);
      return acc;
    },
    {} as Record<string, any[]>,
  );

  const dealProducts = (deal.products ?? []).filter((x) => !x.retired);
  const allProductsAssigned =
    dealProducts.length > 0 && domainProducts.length >= dealProducts.length;

  const isAddDisabled = !isActive || allProductsAssigned;

  const addProductsButton = (
    <LinkButton
      titleText="Add New Products to Domain"
      icon={PlusDefaultOutlineIcon}
      iconAlign="left"
      variant="outline-secondary"
      size="xs"
      className={`w-100${isAddDisabled ? ` ${styles.disabledLink}` : ''}`}
      disabled={isAddDisabled}
      onClick={() => setShowAddProductsModal(true)}
    />
  );

  return (
    <div className="mb-3">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-2 gap-2">
        <div>Products:</div>
        {!readOnly && (
          <div className="d-grid d-md-block mb-2 mb-md-0">
            {allProductsAssigned ? (
              <Tooltip
                content="Already added all available products to this domain"
                type="sm"
                placement="left"
                className="darkTooltip"
              >
                <span className="d-flex">{addProductsButton}</span>
              </Tooltip>
            ) : (
              addProductsButton
            )}
          </div>
        )}
      </div>
      <div className={selectContainerStyles.selectContainer}>
        {Object.entries(productsByType).map(([type, typeProducts]) => (
          <div key={type} className="mb-2">
            <div className={`${selectContainerStyles.categorySection} mb-1`}>
              {type ? <WizardCategoryLabel type={type as CategoryType} /> : '-'}
            </div>
            <div className={styles.accessRuleProducts}>
              {typeProducts.map((product: any, index: number) => (
                <div
                  key={product.sku}
                  className={`${selectContainerStyles.typography12} ${selectContainerStyles.gray600}`}
                >
                  <div
                    className={`${styles.accessRuleProductItem} d-flex gap-2 align-items-center`}
                  >
                    <RenderHtml html={product.name || product.sku} tag="div" />
                  </div>
                  {index < typeProducts.length - 1 && <hr />}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {showAddProductsModal && (
        <AddProductsToDomainModal
          deal={deal}
          rule={rule}
          onClose={() => {
            setShowAddProductsModal(false);
          }}
          onSuccess={() => {
            setShowAddProductsModal(false);
            onEditModeChange?.(false);
          }}
          disabledProductIds={assignedProductsWithInfo.map(
            (p) => p.accountProductId,
          )}
        />
      )}
    </div>
  );
}
